/**
 * Download-on-demand chart PACKS: manifest parsing and chart mapping.
 *
 * The flow, kept as pure functions so it is unit-testable without a browser:
 *   - A remote `packs.json` lists the available packs (region, size, bounds, and
 *     the URLs its charts live at). `parsePacksManifest` validates it.
 *   - Each pack has its own per-chart manifest (same shape as the bundled BSB
 *     manifest, but every chart's `path` is a bare PMTiles file name).
 *     `mapPackCharts` turns those into real RasterCharts, rewriting each `path`
 *     and `source` for how the pack is being used: streamed from the remote host
 *     (view-online) or read from a downloaded copy on the device (offline).
 *
 * Nothing here does any Capacitor/native work; the download side lives in
 * packDownload.ts. This split keeps the parsing/mapping testable headless.
 */
import type { CellMeta } from '@/types';
import { authedPackUrl } from './chartAccess';
import type { ChartPack, RasterChart, RasterSource } from './providers';

function isFiniteNumber(n: unknown): n is number {
  return typeof n === 'number' && Number.isFinite(n);
}

function isBounds(b: unknown): b is [number, number, number, number] {
  return Array.isArray(b) && b.length === 4 && b.every(isFiniteNumber);
}

/** Validate one pack entry from a packs manifest. */
export function isChartPack(c: unknown): c is ChartPack {
  if (!c || typeof c !== 'object') return false;
  const r = c as Record<string, unknown>;
  return (
    typeof r.id === 'string' &&
    typeof r.region === 'string' &&
    isBounds(r.bounds) &&
    isFiniteNumber(r.sizeBytes) &&
    isFiniteNumber(r.chartCount) &&
    typeof r.chartsUrl === 'string' &&
    typeof r.baseUrl === 'string'
  );
}

/**
 * Resolve one of a pack's URLs against wherever the manifest was fetched from.
 *
 * A pack's files sit next to its manifest, so the manifest should say
 * "puerto-rico.cells.json" and let the reader work out the rest. It used to bake
 * absolute URLs at build time from --owner/--repo, which meant the published
 * manifest hard-coded one host: move the bucket, change the CDN, or just forget
 * the flags (the defaults were the literal strings OWNER and REPO) and every pack
 * pointed somewhere that did not exist, while the manifest itself loaded fine.
 *
 * An absolute URL is passed through untouched, so manifests already published in
 * the old shape keep working.
 */
function resolveAgainst(value: string, manifestUrl: string | undefined): string {
  if (!manifestUrl || /^[a-z][a-z0-9+.-]*:/i.test(value)) return value;
  try {
    return new URL(value, manifestUrl).toString();
  } catch {
    return value;
  }
}

/**
 * Parse + validate a packs manifest document into a clean pack list.
 *
 * Pass the URL the manifest came from and any relative pack URLs are resolved
 * against it, so what comes out is always absolute and nothing downstream has to
 * care which form the manifest used.
 */
export function parsePacksManifest(data: unknown, manifestUrl?: string): ChartPack[] {
  const packs = (data as { packs?: unknown })?.packs;
  if (!Array.isArray(packs)) return [];
  return packs.filter(isChartPack).map((p) => ({
    ...p,
    chartsUrl: resolveAgainst(p.chartsUrl, manifestUrl),
    baseUrl: resolveAgainst(p.baseUrl, manifestUrl),
    ...(p.cellsUrl ? { cellsUrl: resolveAgainst(p.cellsUrl, manifestUrl) } : {}),
  }));
}

/** A per-pack chart entry: the bundled RasterChart shape, `path` = bare file name. */
export function isPackChart(c: unknown): c is RasterChart {
  if (!c || typeof c !== 'object') return false;
  const r = c as Record<string, unknown>;
  return (
    typeof r.id === 'string' &&
    typeof r.name === 'string' &&
    isBounds(r.bounds) &&
    typeof r.path === 'string' &&
    isFiniteNumber(r.tileMinZoom) &&
    isFiniteNumber(r.tileMaxZoom) &&
    isFiniteNumber(r.nativeZoom)
  );
}

/** Parse a per-pack chart manifest document into raw pack charts (path = file name). */
export function parsePackCharts(data: unknown): RasterChart[] {
  const charts = (data as { charts?: unknown })?.charts;
  return Array.isArray(charts) ? charts.filter(isPackChart) : [];
}

/** Just the file name of a chart's `path` (packs store bare names, but be lenient). */
export function chartFileName(path: string): string {
  const parts = path.split('/');
  return parts[parts.length - 1] || path;
}

/**
 * Join a base URL and a file name into one absolute URL, tolerating a missing or
 * present trailing slash on the base.
 */
export function joinUrl(base: string, name: string): string {
  return `${base.replace(/\/+$/, '')}/${name}`;
}

/**
 * Map a pack's raw charts onto real RasterCharts for a given use: resolve(file)
 * returns the { path, source } for each chart, so the SAME mapper serves
 * view-online (remote URL) and offline (local file URI). Chart ids are namespaced
 * by pack so two packs (or a pack and the bundled set) can't collide.
 */
export function mapPackCharts(
  pack: ChartPack,
  rawCharts: RasterChart[],
  resolve: (fileName: string) => { path: string; source: RasterSource },
): RasterChart[] {
  return rawCharts.map((c) => {
    const file = chartFileName(c.path);
    const { path, source } = resolve(file);
    return { ...c, id: `pack:${pack.id}:${c.id}`, path, source };
  });
}

/** Map a pack's raw charts to REMOTE charts streamed from its host (view-online). */
export function remotePackCharts(pack: ChartPack, rawCharts: RasterChart[]): RasterChart[] {
  return mapPackCharts(pack, rawCharts, (file) => ({
    // authedPackUrl carries the access token onto private-host URLs, so the
    // PMTiles reader's range requests get through the charts-gate.
    path: authedPackUrl(joinUrl(pack.baseUrl, file)),
    source: 'remote',
  }));
}

/** Fetch + parse a packs manifest from a URL. Returns [] on any failure. */
export async function loadPacksManifest(url: string): Promise<ChartPack[]> {
  try {
    return await fetchPacksManifest(url);
  } catch {
    return [];
  }
}

/**
 * Like loadPacksManifest but THROWS on a network error or non-2xx response, so
 * the caller can tell "the list couldn't be reached" (show Retry) apart from
 * "the list loaded and is genuinely empty" (show a plain empty state).
 */
export async function fetchPacksManifest(url: string, timeoutMs = 10_000): Promise<ChartPack[]> {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), timeoutMs);
  try {
    const res = await fetch(authedPackUrl(url), { signal: ctrl.signal });
    if (!res.ok) throw new Error(`Packs manifest ${res.status}`);
    return parsePacksManifest(await res.json(), url);
  } finally {
    clearTimeout(timer);
  }
}

/** A vector pack's cell entry: the CellMeta the ingest writes, provenance included. */
export function isPackCell(c: unknown): c is CellMeta {
  if (!c || typeof c !== 'object') return false;
  const r = c as Record<string, unknown>;
  return (
    typeof r.id === 'string' &&
    isBounds(r.bounds) &&
    isFiniteNumber(r.scaleBand)
  );
}

/** Parse a vector pack's cell manifest document. */
export function parsePackCells(data: unknown): CellMeta[] {
  const cells = (data as { cells?: unknown })?.cells;
  return Array.isArray(cells) ? cells.filter(isPackCell) : [];
}

/**
 * Fetch + parse a vector pack's cell manifest. Returns [] on any failure, the
 * same way the raster side does, so one unreachable pack cannot break the list.
 */
export async function loadPackCellManifest(pack: ChartPack): Promise<CellMeta[]> {
  const url = pack.cellsUrl ?? pack.chartsUrl;
  try {
    const res = await fetch(authedPackUrl(url));
    if (!res.ok) return [];
    return parsePackCells(await res.json());
  } catch {
    return [];
  }
}

/** Fetch + parse a pack's per-chart manifest. Returns [] on any failure. */
export async function loadPackChartManifest(pack: ChartPack): Promise<RasterChart[]> {
  try {
    const res = await fetch(authedPackUrl(pack.chartsUrl));
    if (!res.ok) return [];
    return parsePackCharts(await res.json());
  } catch {
    return [];
  }
}
